import { apiErrorSchema, type ApiErrorCode } from '@product-portal/shared';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Same-origin calls to the BFF. Authentication is the httpOnly session cookie,
 * sent by the browser: no token is ever read, stored or attached here.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // FormData sets its own multipart boundary.
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  if (response.status === 204) return undefined as T;
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const issues = (payload as { issues?: unknown } | null)?.issues;
    if (response.status === 400 && Array.isArray(issues)) {
      throw new ApiValidationError((payload as { message?: string }).message ?? 'Requête invalide.', issues as ApiValidationError['issues']);
    }
    const parsed = apiErrorSchema.safeParse(payload);
    throw new ApiRequestError(
      response.status,
      parsed.success ? parsed.data.code : 'UNKNOWN',
      parsed.success ? parsed.data.message : response.statusText,
    );
  }
  return payload as T;
}

export function isApiError(error: unknown, status: number, code?: string): error is ApiRequestError {
  return error instanceof ApiRequestError && error.status === status && (code === undefined || error.code === code);
}

/** Validation issues of a 400 response (Zod issues from the API). */
export class ApiValidationError extends ApiRequestError {
  constructor(
    message: string,
    readonly issues: { path: (string | number)[]; message: string }[],
  ) {
    super(400, 'BAD_REQUEST', message);
  }
}
